import { ExternalLink, FileText, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { RelativeTime, StatusPill, UserAvatar } from '@/shared/components/common'
import { EmptyState, ErrorState, SkeletonRows } from '@/shared/components/data'
import { Button } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useDocuments } from './api'
import { DocumentForm } from './components/DocumentForm'
import { docTypeLabel, type DocumentFilters } from './types'

export interface DocumentsListProps {
  companyId: string
  projectId?: string | null
  filters: DocumentFilters
  /** Prefijo relativo del link al documento: normalmente `''` (misma ruta). */
  canWrite: boolean
  /** Tipo con el que se crean los documentos nuevos desde esta lista. */
  defaultDocType?: string
  emptyTitle: string
  emptyDescription: string
}

/**
 * Lista de documentos.
 *
 * Los `markdown` abren el viewer/editor de la app; los `link` abren afuera y no
 * tienen editor. La distinción se ve en el ícono antes de hacer click, que es
 * cuando sirve.
 */
export function DocumentsList({
  companyId,
  projectId = null,
  filters,
  canWrite,
  defaultDocType = 'note',
  emptyTitle,
  emptyDescription,
}: DocumentsListProps) {
  const t = useT()
  const query = useDocuments(filters)
  const [formOpen, setFormOpen] = useState(false)

  if (query.isPending) return <SkeletonRows rows={4} columns={[0.4, 0.2, 0.2]} withSpine={false} />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />

  const documents = query.data ?? []

  return (
    <div className="flex flex-col gap-3">
      {canWrite ? (
        <div className="flex justify-end">
          <Button variant="primary" onClick={() => setFormOpen(true)}>
            <Plus size={13} aria-hidden="true" />
            {t('docs.new')}
          </Button>
        </div>
      ) : null}

      {documents.length === 0 ? (
        <EmptyState
          title={emptyTitle}
          description={emptyDescription}
          icon={<FileText size={18} aria-hidden="true" />}
          action={
            canWrite ? (
              <Button variant="primary" onClick={() => setFormOpen(true)}>
                {t('docs.createFirst')}
              </Button>
            ) : null
          }
        />
      ) : (
        <ul className="hairline flex flex-col overflow-hidden rounded-[var(--radius-card)] bg-surface-1">
          {documents.map((document) => (
            <li
              key={document.id}
              className="hairline-b flex flex-wrap items-center gap-2 px-3 py-2 last:border-b-0"
            >
              {document.kind === 'link' ? (
                <ExternalLink size={13} aria-hidden="true" className="text-text-3" />
              ) : (
                <FileText size={13} aria-hidden="true" className="text-text-3" />
              )}

              {document.kind === 'link' && document.external_url ? (
                <a
                  href={document.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-text-1 hover:text-signal"
                >
                  {document.title}
                </a>
              ) : (
                <Link to={document.id} className="text-xs text-text-1 hover:text-signal">
                  {document.title}
                </Link>
              )}

              <span className="text-2xs text-text-3">{docTypeLabel(t, document.doc_type)}</span>

              <StatusPill tone={document.visibility === 'client' ? 'in_development' : 'neutral'}>
                {document.visibility === 'client'
                  ? t('docs.visibility.client')
                  : t('docs.visibility.internal')}
              </StatusPill>

              <span className="num text-2xs text-text-3">
                {t('docs.version', { version: document.current_version })}
              </span>

              <span className="flex-1" />
              <UserAvatar user={document.updated_by} />
              <RelativeTime value={document.updated_at} />
            </li>
          ))}
        </ul>
      )}

      {canWrite ? (
        <DocumentForm
          companyId={companyId}
          projectId={projectId}
          defaultDocType={defaultDocType}
          open={formOpen}
          onClose={() => setFormOpen(false)}
        />
      ) : null}
    </div>
  )
}
